import { useCallback, useMemo, useState, type SyntheticEvent } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import iconDeleteMatchCondition from "../../../assets/icon-delete-match-condition.svg";
import type {
  OtoDimensionOption,
  OtoMatchConditionDraft,
} from "../../../services/objectToObjectPlanService";
import {
  createEmptyMatchConditionDraft,
  formatOtoWeightForInput,
  isAllowedOtoWeightInput,
  OTO_ONE_SIDED_DIMENSION_WARNING,
  otoDimensionAvailabilityLabel,
} from "../../../services/objectToObjectPlanService";

/** Normalize Weight field changes from typing or native spinner (step 0.1). */
function normalizeWeightFieldChange(raw: string): string | null {
  const value = raw.trim();
  if (value === "") return "";
  if (isAllowedOtoWeightInput(value)) return value;
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0 || numeric > 1) return null;
  const rounded = Math.round(numeric * 10) / 10;
  if (rounded < 0 || rounded > 1) return null;
  // Accept float noise from steppers (e.g. 0.30000000004), reject 0.55-style input.
  if (Math.abs(numeric - rounded) >= 1e-8) return null;
  return formatOtoWeightForInput(rounded);
}

const COL_NO_WIDTH = 56;
/** Wide enough for validation copy like "Weight must be between 0 and 1…". */
const COL_WEIGHT_WIDTH = 200;
const COL_ACTION_WIDTH = 72;
/** Horizontal inset for header bar (margin) and title alignment. */
const CONTENT_PX = 3; // 24px
/** Inner padding inside the gray header — also applied to body for column align. */
const HEADER_INNER_PX = 1.5; // 12px
const BODY_PX = CONTENT_PX + HEADER_INNER_PX; // 36px
const SELECT_MENU_MAX_HEIGHT = 280;
const SELECT_MENU_EDGE_GAP = 8;

const rowGridSx = {
  display: "grid",
  gridTemplateColumns: `${COL_NO_WIDTH}px minmax(0, 1fr) ${COL_WEIGHT_WIDTH}px ${COL_ACTION_WIDTH}px`,
  columnGap: 2,
  alignItems: "center",
};

const headerLabelSx = {
  fontSize: "14px",
  fontWeight: 600,
  color: "#262626",
  lineHeight: 1.4,
};

/**
 * Prefer opening the Select menu below the field; flip above when the
 * viewport has more room upward (and not enough downward).
 */
function useAdaptiveSelectMenuProps(maxHeight = SELECT_MENU_MAX_HEIGHT) {
  const [placement, setPlacement] = useState<"bottom" | "top">("bottom");
  const [paperMaxHeight, setPaperMaxHeight] = useState(maxHeight);

  const handleOpen = useCallback(
    (event: SyntheticEvent) => {
      const anchor = event.currentTarget as HTMLElement | null;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - SELECT_MENU_EDGE_GAP;
      const spaceAbove = rect.top - SELECT_MENU_EDGE_GAP;
      const preferTop =
        spaceBelow < Math.min(maxHeight, 200) && spaceAbove > spaceBelow;
      const available = preferTop ? spaceAbove : spaceBelow;
      setPlacement(preferTop ? "top" : "bottom");
      setPaperMaxHeight(
        Math.max(120, Math.min(maxHeight, Math.floor(available))),
      );
    },
    [maxHeight],
  );

  const MenuProps = useMemo(
    () => ({
      PaperProps: { sx: { maxHeight: paperMaxHeight } },
      anchorOrigin: {
        vertical: (placement === "bottom" ? "bottom" : "top") as
          | "bottom"
          | "top",
        horizontal: "left" as const,
      },
      transformOrigin: {
        vertical: (placement === "bottom" ? "top" : "bottom") as
          | "bottom"
          | "top",
        horizontal: "left" as const,
      },
      marginThreshold: SELECT_MENU_EDGE_GAP,
    }),
    [paperMaxHeight, placement],
  );

  return { onOpen: handleOpen, MenuProps };
}

type MatchConditionsEditorProps = Readonly<{
  rows: OtoMatchConditionDraft[];
  dimensionOptions: OtoDimensionOption[];
  /** Max dimensions from admin-flags `object_to_object.max_dimensions_per_plan`. */
  maxDimensions?: number | null;
  dimensionsLoading?: boolean;
  dimensionsError?: string | null;
  showValidation?: boolean;
  formError?: string;
  rowErrors?: Record<string, { dimension?: string; weight?: string }>;
  onChange: (rows: OtoMatchConditionDraft[]) => void;
}>;

type MatchConditionRowSelectsProps = Readonly<{
  row: OtoMatchConditionDraft;
  index: number;
  dimensionOptions: OtoDimensionOption[];
  availableOptions: OtoDimensionOption[];
  dimensionSelectDisabled: boolean;
  dimensionsLoading: boolean;
  showValidation: boolean;
  errors?: { dimension?: string; weight?: string };
  oneSided: boolean;
  sideHint: string | null;
  onDimensionChange: (id: string, code: string) => void;
  onWeightChange: (id: string, weight: string) => void;
  onRemove: (id: string) => void;
}>;

function MatchConditionRowSelects({
  row,
  index,
  dimensionOptions,
  availableOptions,
  dimensionSelectDisabled,
  dimensionsLoading,
  showValidation,
  errors,
  oneSided,
  sideHint,
  onDimensionChange,
  onWeightChange,
  onRemove,
}: MatchConditionRowSelectsProps) {
  const dimensionMenu = useAdaptiveSelectMenuProps();

  const weightError =
    showValidation && errors?.weight ? errors.weight : undefined;
  const dimensionError =
    showValidation && errors?.dimension ? errors.dimension : undefined;

  return (
    <Box
      sx={{
        ...rowGridSx,
        px: BODY_PX,
        py: 1.5,
        borderBottom: "1px solid #F0F0F0",
        "&:last-of-type": { borderBottom: "none" },
        // Keep Dimension / Weight inputs top-aligned when helper text appears.
        alignItems: "flex-start",
      }}
    >
      <Typography
        sx={{
          color: "#595959",
          fontSize: "14px",
          lineHeight: "40px",
        }}
      >
        {index + 1}
      </Typography>

      <Box sx={{ minWidth: 0 }}>
        <FormControl fullWidth size="small" error={Boolean(dimensionError)}>
          <Select
            displayEmpty
            value={row.dimensionCode}
            disabled={dimensionSelectDisabled}
            onOpen={dimensionMenu.onOpen}
            onChange={(event) =>
              onDimensionChange(row.id, String(event.target.value))
            }
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Box component="span" sx={{ color: "#9ca3af" }}>
                    {dimensionsLoading
                      ? "Loading dimensions…"
                      : "Select Dimension"}
                  </Box>
                );
              }
              const matched = dimensionOptions.find(
                (item) => item.code === selected,
              );
              const hint = matched
                ? otoDimensionAvailabilityLabel(matched.availability)
                : null;
              const label = matched?.name || selected;
              return hint ? `${label} (${hint})` : label;
            }}
            MenuProps={dimensionMenu.MenuProps}
            sx={{ height: 40 }}
          >
            {availableOptions.map((option) => {
              const hint = otoDimensionAvailabilityLabel(option.availability);
              return (
                <MenuItem key={option.code} value={option.code}>
                  {hint
                    ? `${option.name || option.code} (${hint})`
                    : option.name || option.code}
                </MenuItem>
              );
            })}
          </Select>
          {dimensionError ? (
            <FormHelperText sx={{ mx: 0, mt: 0.5 }}>
              {dimensionError}
            </FormHelperText>
          ) : null}
        </FormControl>
        {oneSided ? (
          <Alert
            severity="warning"
            sx={{
              mt: 1,
              py: 0,
              px: 1.25,
              "& .MuiAlert-message": { fontSize: "12px" },
            }}
          >
            {sideHint
              ? `${OTO_ONE_SIDED_DIMENSION_WARNING} (${sideHint})`
              : OTO_ONE_SIDED_DIMENSION_WARNING}
          </Alert>
        ) : null}
      </Box>

      <TextField
        fullWidth
        size="small"
        type="number"
        placeholder="Weight"
        value={row.weight}
        // Native steppers / ArrowUp|Down move by 0.1 within [0, 1].
        slotProps={{
          htmlInput: {
            min: 0,
            max: 1,
            step: 0.1,
            "aria-label": `Weight for match condition ${index + 1}`,
          },
        }}
        error={Boolean(weightError)}
        helperText={weightError}
        FormHelperTextProps={{
          sx: { mx: 0, mt: 0.5, whiteSpace: "normal" },
        }}
        onKeyDown={(event) => {
          if (["e", "E", "+", "-"].includes(event.key)) {
            event.preventDefault();
          }
        }}
        onChange={(event) => {
          const next = normalizeWeightFieldChange(
            event.target.value.replace(",", "."),
          );
          if (next === null) return;
          onWeightChange(row.id, next);
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            height: 40,
            borderRadius: "3px",
            fontSize: "0.875rem",
          },
        }}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 40,
        }}
      >
        <IconButton
          aria-label={`Remove match condition ${index + 1}`}
          onClick={() => onRemove(row.id)}
          size="small"
          sx={{ color: "#CF1322" }}
        >
          <Box
            component="img"
            src={iconDeleteMatchCondition}
            alt=""
            sx={{ width: 18, height: 18 }}
          />
        </IconButton>
      </Box>
    </Box>
  );
}

export default function MatchConditionsEditor({
  rows,
  dimensionOptions,
  maxDimensions = null,
  dimensionsLoading = false,
  dimensionsError = null,
  showValidation = false,
  formError,
  rowErrors = {},
  onChange,
}: MatchConditionsEditorProps) {
  const atMax =
    typeof maxDimensions === "number" &&
    maxDimensions > 0 &&
    rows.length >= maxDimensions;
  const dimensionSelectDisabled = dimensionsLoading || Boolean(dimensionsError);

  const handleAdd = () => {
    if (atMax) return;
    onChange([...rows, createEmptyMatchConditionDraft()]);
  };

  const handleRemove = (id: string) => {
    onChange(rows.filter((row) => row.id !== id));
  };

  const handleDimensionChange = (id: string, code: string) => {
    const matched = dimensionOptions.find((item) => item.code === code);
    onChange(
      rows.map((row) =>
        row.id === id
          ? {
              ...row,
              dimensionCode: code,
              dimensionLabel: matched?.name || code,
            }
          : row,
      ),
    );
  };

  const handleWeightChange = (id: string, weight: string) => {
    onChange(
      rows.map((row) => (row.id === id ? { ...row, weight } : row)),
    );
  };

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "8px",
        border: "none",
        boxShadow: "none",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: CONTENT_PX,
          pt: 2,
          pb: 1.5,
        }}
      >
        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#262626" }}>
          Match Conditions{" "}
          <Box component="span" sx={{ fontWeight: 400, color: "#8c8c8c" }}>
            ({rows.length})
          </Box>
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={atMax || dimensionSelectDisabled}
          sx={{ textTransform: "none" }}
        >
          Add Dimension
        </Button>
      </Box>

      {formError ? (
        <FormHelperText error sx={{ mx: CONTENT_PX, mt: 0, mb: 1 }}>
          {formError}
        </FormHelperText>
      ) : null}
      {dimensionsError ? (
        <FormHelperText error sx={{ mx: CONTENT_PX, mt: 0, mb: 1 }}>
          {dimensionsError}
        </FormHelperText>
      ) : null}

      <Box
        sx={{
          ...rowGridSx,
          px: BODY_PX,
          py: 1.25,
          mx: CONTENT_PX,
          mb: 0,
          bgcolor: "#F5F5F5",
          borderRadius: "6px",
        }}
      >
        <Typography sx={headerLabelSx}>NO.</Typography>
        <Typography sx={headerLabelSx}>Dimension</Typography>
        <Typography sx={headerLabelSx}>WEIGHT</Typography>
        <Typography sx={{ ...headerLabelSx, textAlign: "center" }}>
          ACTION
        </Typography>
      </Box>

      <Box sx={{ pb: 1 }}>
        {rows.length === 0 ? (
          <Typography
            sx={{
              px: BODY_PX,
              py: 2,
              fontSize: "13px",
              color: "#8c8c8c",
            }}
          >
            No dimensions yet. Click Add Dimension to start.
          </Typography>
        ) : (
          rows.map((row, index) => {
            const errors = rowErrors[row.id];
            const selected = dimensionOptions.find(
              (item) => item.code === row.dimensionCode,
            );
            const oneSided =
              Boolean(selected) && selected!.availability !== "both";
            const sideHint = selected
              ? otoDimensionAvailabilityLabel(selected.availability)
              : null;
            const usedCodes = new Set(
              rows
                .filter((item) => item.id !== row.id && item.dimensionCode)
                .map((item) => item.dimensionCode),
            );
            const availableOptions = dimensionOptions.filter(
              (option) =>
                option.code === row.dimensionCode || !usedCodes.has(option.code),
            );

            return (
              <MatchConditionRowSelects
                key={row.id}
                row={row}
                index={index}
                dimensionOptions={dimensionOptions}
                availableOptions={availableOptions}
                dimensionSelectDisabled={dimensionSelectDisabled}
                dimensionsLoading={dimensionsLoading}
                showValidation={showValidation}
                errors={errors}
                oneSided={oneSided}
                sideHint={sideHint}
                onDimensionChange={handleDimensionChange}
                onWeightChange={handleWeightChange}
                onRemove={handleRemove}
              />
            );
          })
        )}
      </Box>
    </Box>
  );
}
